// Coordination areas - the 2D sheet as a way of reading the programme.
//
// The sheet renders underneath; each area is a transparent coloured
// polygon on top of it. Clicking one shows what it covers - the
// activities, and through them the issues and checklists - so the
// drawing answers "what is happening in the OR suite" without anybody
// reading a WBS.
//
// EDITING IS OWNER-ONLY (enforced by the API too): an area's colour
// drives the colour of everything tied to it, so a contractor redrawing
// a zone would be redrawing the master's view of the job. Everything an
// extraction proposes waits under "Waiting for you" until validated.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Button, Select, Input, Tag, Modal, message, Empty, Spin, Tooltip, Popconfirm, Badge,
  Drawer, List, Checkbox,
} from "antd";
import { activityLabel, activityTitle, withActivityPaths } from "helpers/schedule/activityLabel";
import {
  PlusOutlined, DeleteOutlined, ScanOutlined, CheckOutlined, CloseOutlined, EditOutlined,
  UploadOutlined, HistoryOutlined, ShareAltOutlined, SearchOutlined, FilePdfOutlined,
  PrinterOutlined, FileExcelOutlined,
} from "@ant-design/icons";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import AreaShapePreview from "components/Coordination/AreaShapePreview";
import AreaMapRevisionPanel from "components/Coordination/AreaMapRevisionPanel";
import AreaLegendStrip from "components/Coordination/AreaLegendStrip";
import AreaShopDrawings from "pages/ShopDrawings/AreaShopDrawings";
import ShareResourceModal from "components/Share/ShareResourceModal";
import IssueTool from "components/DocumentControl/IssueTool/IssueTool";
import { url, tokenRef } from "api";
import {
  listAreas, listPendingAreas, createArea, updateArea, deleteArea, decideArea, extractAreas,
  listAreaSheets, areaTimeline, importAreaSheet, areaCoverage, assignToArea,
  listAreaMigrations, restoreArea, areaHistory, getAreaLegend,
  listLevels, addLevel, updateLevel, removeLevel, levelsFromSheet, listAreaSystems, listAreaSystemsByLevel,
  listIssueLabels,
} from "api/areas/areasApi";
import { buildLevelsExport, downloadWorkbook, printHtml } from "./areasExport";
import AreaSystemsPanel, { placementLine, placementNeedsAction } from "components/Coordination/AreaSystemsPanel";
import OwnerSignOffToggle, { findToggle } from "components/Coordination/OwnerSignOffToggle";
import OwnerSignOffRecipients from "components/Coordination/OwnerSignOffRecipients";
import { listOwnerSignOffs, setOwnerSignOff, withdrawOwnerSignOff } from "api/ownerSignOff/ownerSignOffApi";
import { useRememberedProject } from "helpers/projectSelection";
import { matchOption } from "components/SearchSelect/searchSelect";

// WHAT THIS PERSON MAY DO HERE (2026-10-14: levelsAreasView / Add / Edit /
// Delete). The API puts canAdd / canEdit / canDelete on every payload the
// page reads (GET /areas, /levels, /legend, /systems) and refuses the write
// the same way, so a control is only shown when it would be accepted.
// Print and Export Excel need view only.
export function areaCaps(payload) {
  const p = payload || {};
  return { canAdd: !!p.canAdd, canEdit: !!p.canEdit, canDelete: !!p.canDelete };
}
// A 403 that names levelsAreasView: the tab is not this person's to read.
export function isViewRefusal(e) {
  return !!(e && e.status === 403);
}

const PALETTE = ["#3b82f6", "#22c55e", "#a855f7", "#f97316", "#ef4444", "#06b6d4", "#eab308", "#ec4899"];

export default function CoordinationAreasPage() {
  const { projectId: routeProjectId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const svgRef = useRef(null);
  const fileRef = useRef(null);
  const userInfo = useSelector((st) => st.user.userInfo) || {};

  // Same project picker every other coordination page uses, so landing
  // here without one in the URL does not show an empty drawing board.
  const [projects, setProjects] = useState([]);
  // NOTE the hook returns a TUPLE. Destructuring it as an object left
  // setProjectId undefined, so touching the project dropdown threw and
  // blanked the page.
  const [pickedId, setProjectId] = useRememberedProject(
    String(userInfo._id || userInfo.id || ""), projects, routeProjectId
  );
  const projectId = pickedId || routeProjectId;

  const [sheets, setSheets] = useState([]);
  const [sheetDocId, setSheetDocId] = useState("");
  // WHICH SHEET of the set is on screen. A controlled document is
  // routinely several drawings - the second floor is not another version
  // of the first - so the page is part of what you are looking at, and
  // only the zones drawn on THIS page belong on it.
  const [pageIdx, setPageIdx] = useState(0);
  const [timeline, setTimeline] = useState([]);
  const [coverage, setCoverage] = useState(null);
  // Issue area labels / tags and the areas they land in (GET .../issue-labels).
  const [issueLabels, setIssueLabels] = useState([]);
  const [unplacedOpen, setUnplacedOpen] = useState(false);
  const [picked, setPicked] = useState({ activities: [], milestones: [], issues: [], checklists: [] });
  const [assignTo, setAssignTo] = useState("");
  const [importing, setImporting] = useState(false);

  const [areas, setAreas] = useState([]);
  // LEVELS & AREAS (owner, 2026-10-10): one page per level, each with its
  // own name and drawing, showing its own areas or the first page's.
  const [levels, setLevels] = useState({ pages: [], saved: false });
  const [levelIdx, setLevelIdx] = useState(0);
  const [levelName, setLevelName] = useState("");
  const activeLevel = (levels.pages || [])[levelIdx] || null;
  // Showing ANOTHER level's sheet and areas (any level, the first included).
  const sharedLevel = !!(activeLevel && activeLevel.areasFrom && activeLevel.areasFrom !== "own");
  const [pending, setPending] = useState([]);
  // What a re-issued drawing proposes about the zones already on the
  // job - which new shape replaces which old one, and what would move
  // with it. A proposal, decided row by row.
  const [migrations, setMigrations] = useState([]);
  // Zones a newer drawing superseded. Off the live map, still readable -
  // and restorable, because "not on the new sheet" is a judgement about
  // a drawing, not a fact.
  const [retired, setRetired] = useState([]);
  const [retiredOpen, setRetiredOpen] = useState(false);
  // Reading a PAST map. "" is now/live; a YYYY-MM-DD asks the API for
  // the zones that were agreed on that day - a superseded zone is
  // retired, not deleted, so the drawing people were building to then
  // still resolves.
  const [asOf, setAsOf] = useState("");
  const [checkpoints, setCheckpoints] = useState([]);
  const [legend, setLegend] = useState(null);
  const [caps, setCaps] = useState({ canAdd: false, canEdit: false, canDelete: false });
  const { canAdd, canEdit, canDelete } = caps;
  const [viewDenied, setViewDenied] = useState(false);
  // Read-only sharing of THIS page with partners / no-account guests.
  const [shareOpen, setShareOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sheetUrl, setSheetUrl] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [activities, setActivities] = useState([]);
  // THE PARENT PATH ON EVERY OPTION. Owner, 2026-10-01: "make sure all
  // activities have the sequence from parent to child using the ... for long
  // names, and make sure the tool tip shows everything". Derived from the FULL
  // list before anything filters it - an ancestor removed by a filter is an
  // ancestor the walk cannot resolve, and the helper stops at a gap rather
  // than joining across it and inventing a shorter hierarchy.
  const activityOptions = useMemo(() => withActivityPaths(activities), [activities]);

  // Drawing state: a click drops a point, double-click closes the shape.
  // Editing happens on the markup canvas people already use for issues
  // and sheets, not in a bespoke editor: the same shape tools, the same
  // fill-colour picker (now with a see-through slider), the same
  // keyboard shortcuts. The AI's polygons arrive as ordinary shapes, so
  // adjusting one is dragging a handle rather than re-running anything.
  const [canvasOpen, setCanvasOpen] = useState(false);
  const [sheetSize, setSheetSize] = useState({ w: 0, h: 0 });

  const [drawing, setDrawing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(PALETTE[0]);

  const auth = useCallback(() => {
    const t = localStorage.getItem(tokenRef);
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  useEffect(() => {
    fetch(`${url}/projects/picker`, { headers: auth() })
      .then((r) => r.json())
      .then((d) => setProjects(Array.isArray(d) ? d : d?.projects || []))
      .catch(() => setProjects([]));
  }, [auth]);

  // Keep the URL honest when the picker changes project.
  useEffect(() => {
    if (projectId && projectId !== routeProjectId) history.replace(`/areas/${projectId}`);
  }, [projectId, routeProjectId, history]);

  const loadSheets = useCallback(() => {
    if (!projectId) return;
    listAreaSheets(projectId)
      .then((d) => setSheets(d.sheets || []))
      .catch(() => setSheets([]));
    areaTimeline(projectId)
      .then((d) => setTimeline(d.sheets || []))
      .catch(() => setTimeline([]));
    areaCoverage(projectId)
      .then((d) => setCoverage(d))
      .catch(() => setCoverage(null));
  }, [projectId]);

  // The levels, and which one is on screen. Declared before anything that
  // calls it (chooseSheet, load) - a const read above its declaration is a
  // white page, not an undefined.
  const loadLevels = useCallback(async () => {
    if (!projectId) return;
    try {
      const d = await listLevels(projectId);
      setViewDenied(false);
      setLevels({ pages: d.pages || [], saved: !!d.saved });
      setLevelIdx((i) => Math.min(i, Math.max(0, (d.pages || []).length - 1)));
    } catch (e) {
      setViewDenied(isViewRefusal(e));
      setLevels({ pages: [], saved: false });
    }
  }, [projectId]);

  // SYSTEMS PER AREA (owner, 2026-10-13): each zone's systems and the
  // systems its tagged model elements suggest - one read for the project
  // (GET /areas/systems). When the server answers from a cache a new tag or
  // placement run has made stale, it says `refreshing` and is asked again
  // with fresh=1, so the counts on screen catch up without a reload.
  const [systemsData, setSystemsData] = useState({ byId: {}, vocabulary: [], totals: {}, orphaned: null, computedAt: null, refreshing: false, placement: null });
  const loadSystems = useCallback(async (fresh = false) => {
    if (!projectId) return;
    try {
      const d = await listAreaSystems(projectId, { fresh });
      const byId = {};
      (d.areas || []).forEach((r) => { byId[String(r.areaId)] = r; });
      setSystemsData({ byId, vocabulary: d.vocabulary || [], totals: d.totals || {}, orphaned: d.orphaned || null, computedAt: d.computedAt || null, refreshing: !!d.refreshing, placement: d.placement || null });
      if (d.refreshing && !fresh) loadSystems(true);
    } catch (e) {
      setSystemsData((x) => ({ ...x, byId: {} }));
    }
  }, [projectId]);
  useEffect(() => { loadSystems(false); }, [loadSystems]);
  // OWNER/GC SIGN-OFF (owner, 2026-10-21): the client's sign-off of this
  // level, its areas and their systems, each with an optional day - the blue
  // star on the schedule, and "Signed off by owner/GC" on the issues and shop
  // drawings they reach. Every reply is the whole list.
  const [ownerSO, setOwnerSO] = useState({ toggles: [], canSign: false, failed: false });
  const loadOwnerSO = useCallback(async () => {
    if (!projectId) return;
    try {
      const d = await listOwnerSignOffs(projectId);
      setOwnerSO({ toggles: d.toggles || [], canSign: !!d.canSign, failed: false });
    } catch (e) {
      setOwnerSO({ toggles: [], canSign: false, failed: true });
    }
  }, [projectId]);
  useEffect(() => { loadOwnerSO(); }, [loadOwnerSO]);
  const ownerSOSet = async (body) => {
    try {
      const d = await setOwnerSignOff(projectId, body);
      setOwnerSO((x) => ({ ...x, toggles: d.toggles || [] }));
      const t = (d.toggles || []).find((x) => x.id === d.saved);
      if (t) message.success(`${t.scope}: ${t.planned ? "owner/GC sign-off planned for" : "signed off by owner/GC on"} ${t.date} - the blue star is on the schedule`);
    } catch (e) {
      message.error(e.message || "Could not record the owner/GC sign-off");
    }
  };
  const ownerSOClear = async (t) => {
    try {
      const d = await withdrawOwnerSignOff(projectId, t.id);
      setOwnerSO((x) => ({ ...x, toggles: d.toggles || [] }));
      message.success(`${t.scope}: owner/GC sign-off withdrawn`);
    } catch (e) {
      message.error(e.message || "Could not withdraw the owner/GC sign-off");
    }
  };
  const onSystemsChanged = (areaId, reply) => {
    if (!reply) return;
    setSystemsData((x) => ({
      ...x,
      byId: { ...x.byId, [String(areaId)]: { ...(x.byId[String(areaId)] || {}), ...reply } },
    }));
  };
  // Construction tracking links here with ?area=<id> to accept that area's
  // suggested systems: select it once the areas are in.
  const wantedArea = new URLSearchParams(location.search || "").get("area") || "";

  const togglePick = (kind, id) => setPicked((p) => {
    const has = p[kind].includes(id);
    return { ...p, [kind]: has ? p[kind].filter((x) => x !== id) : [...p[kind], id] };
  });

  const pickedTotal = Object.values(picked).reduce((n, a) => n + a.length, 0);

  // A zone that the map-revision panel above already asks about must not
  // ALSO appear under "Waiting for you" - the same decision in two
  // places, one of which has no idea the other exists.
  const loosePending = useMemo(() => {
    const covered = new Set();
    migrations.forEach((m) => (m.rows || []).forEach((r) => {
      if (r.newAreaId) covered.add(String(r.newAreaId));
    }));
    return pending.filter((p) => !covered.has(String(p._id)));
  }, [pending, migrations]);

  const doAssign = async () => {
    if (!assignTo) { message.warning("Choose the area to put them in"); return; }
    try {
      const r = await assignToArea(projectId, assignTo, picked);
      message.success(`Added to "${r.area.name}"`);
      setPicked({ activities: [], milestones: [], issues: [], checklists: [] });
      loadSheets();
      // Called on a click, long after load is defined below.
      // eslint-disable-next-line no-use-before-define
      load();
    } catch (e) {
      message.error(e.message || "Could not assign");
    }
  };

  useEffect(() => { loadSheets(); }, [loadSheets]);

  // Areas drawn before sheets were filed in Document Control carry only
  // an image path, so match on that to fill the dropdown; and when the
  // project has exactly one sheet and nothing is chosen, choose it.
  useEffect(() => {
    if (sheetDocId || !sheets.length) return;
    // Any page of a set, not only its first: Apple RT06's level stands on
    // page 1 of a set, and a level on page 2 would otherwise find nothing.
    const byUrl = sheetUrl && sheets.find((sh) => sh.url === sheetUrl || (sh.pages || []).includes(sheetUrl));
    if (byUrl) { setSheetDocId(byUrl._id); return; }
    if (!sheetUrl && sheets.length === 1) {
      setSheetDocId(sheets[0]._id);
      setSheetUrl(sheets[0].url);
    }
  }, [sheets, sheetUrl, sheetDocId]);

  // A LEVEL'S DRAWING IS SAVED ON THE LEVEL: choosing a sheet (or a page of
  // it) here sets the drawing for the level on screen.
  const saveLevelDrawing = async (docId, page) => {
    if (!canEdit || !activeLevel || asOf) return;
    try {
      await updateLevel(projectId, activeLevel._id, { sheetDocControlId: docId, sheetPage: page });
      await loadLevels();
    } catch (e) {
      message.error(e.message || "Could not set this level's drawing");
    }
  };

  // ONE LEVEL PER PAGE (owner, 2026-10-10: "any sheet gets selected from the
  // dropdown or uploaded, needs to create 1 page per pdf page, and
  // automatically name them level1, level2, levelx"). A one-page drawing is
  // the drawing for the level on screen, as before; a set is spread over the
  // levels from the first, and the server says what it set, made and kept.
  const spreadOverLevels = async (docId, pageCount) => {
    if (!canAdd || asOf) return false;
    if (!(pageCount > 1)) return false;
    try {
      const r = await levelsFromSheet(projectId, docId);
      const bits = [];
      if (r.created && r.created.length) bits.push(`${r.created.length} level${r.created.length === 1 ? "" : "s"} added (${r.created.map((c) => c.name).join(", ")})`);
      if (r.set && r.set.length) bits.push(`${r.set.map((c) => c.name).join(", ")} set to ${r.set.length === 1 ? "its page" : "their pages"}`);
      message.success(`${r.pageCount} pages: ${bits.join("; ") || "every level already showed its page"}`);
      (r.kept || []).forEach((k) => message.warning(`${k.name} kept its drawing: ${k.why}. Page ${k.page + 1} was not put on it.`, 8));
      await loadLevels();
      setLevelIdx(0);
    } catch (e) {
      message.error(e.message || "Could not set up the levels from that drawing");
    }
    return true;
  };

  const chooseSheet = async (docId) => {
    const s = sheets.find((x) => String(x._id) === String(docId));
    if (s && await spreadOverLevels(docId, s.pageCount)) return;
    setSheetDocId(docId);
    setPageIdx(0);
    if (s) setSheetUrl((s.pages && s.pages[0]) || s.url);
    saveLevelDrawing(docId, 0);
  };

  const currentSheet = useMemo(
    () => sheets.find((x) => String(x._id) === String(sheetDocId)) || null,
    [sheets, sheetDocId]
  );

  const choosePage = (i) => {
    setPageIdx(i);
    setSelectedId(null);
    const pages = (currentSheet && currentSheet.pages) || [];
    if (pages[i]) setSheetUrl(pages[i]);
    if (sheetDocId) saveLevelDrawing(sheetDocId, i);
  };

  // Keep the page dropdown honest when the sheet list arrives after the
  // image did - an area remembers the image it was drawn on, not which
  // page of the set that was.
  useEffect(() => {
    const pages = (currentSheet && currentSheet.pages) || [];
    if (!pages.length || !sheetUrl) return;
    const i = pages.indexOf(sheetUrl);
    if (i >= 0 && i !== pageIdx) setPageIdx(i);
  }, [currentSheet, sheetUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // The zones drawn on the sheet that is on screen.
  //
  // Percentages are per-IMAGE, so painting page 2's polygons over page
  // 1's drawing puts them in meaningless places. A zone with no image
  // recorded predates multi-page sheets and belongs to whatever is
  // showing.
  // THE LEVEL'S AREAS: its own, or the first level's. A past map (asOf)
  // reads by drawing as before - its zones are not on today's pages.
  const pageAreas = useMemo(() => {
    if (activeLevel && !asOf) {
      const ids = new Set((activeLevel.areaIds || []).map(String));
      return areas.filter((a) => ids.has(String(a._id)));
    }
    return areas.filter((a) => !a.sheetImageUrl || !sheetUrl || a.sheetImageUrl === sheetUrl);
  }, [areas, sheetUrl, activeLevel, asOf]);

  // Labels / tags that land in no area. A tag whose name a label already
  // covers ("XC-UTILITIES_COORD" beside "XC-Utilities") is still listed - the
  // two are separate words on the issues and either may be linked.
  const unmatchedLabels = useMemo(
    () => (issueLabels || []).filter((l) => !(l.areas || []).length),
    [issueLabels]
  );
  // What the Also known as picker offers: the unmatched labels, plus this
  // area's own aliases so a chosen one always reads as a chip.
  const aliasOptions = (a) => {
    const seen = new Set();
    const out = [];
    for (const v of [...(a.aliases || []), ...unmatchedLabels.map((l) => l.label)]) {
      const k = String(v).toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      const l = unmatchedLabels.find((x) => x.label === v);
      out.push({ value: v, label: l ? `${v}  ·  ${l.issues} issue${l.issues === 1 ? "" : "s"}${l.kind === "tag" ? " (tag)" : ""}` : v });
    }
    return out;
  };

  const onImportSheet = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const r = await importAreaSheet(projectId, file, file.name);
      message.success(
        `"${r.name}" filed in Document Control${r.renderedFromPdf ? " (page 1 rendered for drawing)" : ""}`
      );
      loadSheets();
      if (r.docControlId && await spreadOverLevels(r.docControlId, r.pageCount)) return;
      setSheetUrl(r.url);
      setSheetDocId(r.docControlId || "");
      if (r.docControlId) saveLevelDrawing(r.docControlId, 0);
    } catch (e) {
      message.error(e.message || "Could not import the sheet");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const load = useCallback(() => {
    if (!projectId) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      listAreas(projectId, undefined, asOf || undefined),
      // A past map has no drafts and no proposals - those are things
      // happening NOW, and showing them beside March's drawing would be
      // inviting a decision about a picture that is not on screen.
      asOf ? Promise.resolve({ areas: [] }) : listPendingAreas(projectId).catch(() => ({ areas: [] })),
      fetch(`${url}/projects/${projectId}/gantt-items`, { headers: auth() })
        .then((r) => r.json())
        .catch(() => []),
      // Owner-only, so a member or partner simply gets nothing back.
      asOf ? Promise.resolve({ migrations: [] }) : listAreaMigrations(projectId).catch(() => ({ migrations: [] })),
      asOf ? Promise.resolve({ areas: [] }) : listAreas(projectId, "historic").catch(() => ({ areas: [] })),
      asOf ? Promise.resolve({ labels: [] }) : listIssueLabels(projectId).catch(() => ({ labels: [] })),
    ])
      .then(([a, p, items, m, h, il]) => {
        loadLevels();
        setIssueLabels((il && il.labels) || []);
        setAreas(a.areas || []);
        setCaps(areaCaps(a));
        setPending(p.areas || []);
        setMigrations(m.migrations || []);
        setRetired(h.areas || []);
        const list = Array.isArray(items) ? items : items?.items || [];
        setActivities(list.filter((i) => i && i.name));
        const withSheet = (a.areas || []).find((x) => x.sheetImageUrl);
        if (withSheet && !sheetUrl) setSheetUrl(withSheet.sheetImageUrl);
        // Preselect the DOCUMENT too, not just its image: the drawing
        // rendered but the dropdown sat on its placeholder, so the page
        // looked like no sheet was chosen while showing one.
        const withDoc = (a.areas || []).find((x) => x.sheetDocControlId);
        if (withDoc && !sheetDocId) setSheetDocId(String(withDoc.sheetDocControlId));
      })
      .catch((e) => message.error(e.message || "Failed to load areas"))
      .finally(() => setLoading(false));
  }, [projectId, auth, sheetUrl, asOf]);

  useEffect(() => { load(); }, [projectId, asOf]); // eslint-disable-line react-hooks/exhaustive-deps

  // SWITCHING LEVEL SHOWS ITS DRAWING. Keyed on the level's own drawing, so
  // a reload that changes nothing about it does not reset the view.
  const levelDrawingKey = activeLevel
    ? `${activeLevel._id}|${activeLevel.sheetDocControlId || ""}|${activeLevel.sheetPage || 0}|${activeLevel.sheetImageUrl || ""}`
    : "";
  useEffect(() => {
    if (!activeLevel || asOf) return;
    setLevelName(activeLevel.name || "");
    setSelectedId(null);
    setDrawing(false); setDraft([]);
    if (activeLevel.sheetImageUrl) {
      setSheetUrl(activeLevel.sheetImageUrl);
      setSheetDocId(activeLevel.sheetDocControlId || "");
      setPageIdx(activeLevel.sheetPage || 0);
    } else {
      setSheetUrl("");
      setSheetDocId("");
      setPageIdx(0);
    }
  }, [levelDrawingKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ?area=<id> (from Construction tracking): go to the level that shows it,
  // then select it - AFTER the level switch above has cleared the selection,
  // which is why this effect is declared below that one.
  const wantedDone = useRef("");
  useEffect(() => {
    if (!wantedArea || wantedDone.current === wantedArea || asOf) return;
    const pages = levels.pages || [];
    if (!pages.length) return;
    const idx = pages.findIndex((p) => (p.areaIds || []).map(String).includes(wantedArea));
    if (idx >= 0 && idx !== levelIdx) { setLevelIdx(idx); return; }
    if (!areas.some((a) => String(a._id) === wantedArea)) return;
    wantedDone.current = wantedArea;
    setSelectedId(wantedArea);
  }, [wantedArea, levels, levelIdx, areas, asOf]);

  // The days the map changed, and the key to its colours. Both belong to
  // the project rather than to the date being viewed, so they load once.
  useEffect(() => {
    if (!projectId) return;
    areaHistory(projectId).then((d) => setCheckpoints(d.checkpoints || [])).catch(() => setCheckpoints([]));
    getAreaLegend(projectId).then(setLegend).catch(() => setLegend(null));
  }, [projectId]);

  useEffect(() => {
    if (!sheetUrl) { setSheetSize({ w: 0, h: 0 }); return; }
    const img = new Image();
    img.onload = () => setSheetSize({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = sheetUrl;
  }, [sheetUrl]);

  // areas (percent) -> canvas shapes (pixels)
  const seededObjects = useMemo(() => {
    if (!sheetSize.w || !sheetSize.h) return null;
    return pageAreas.map((a) => ({
      objectType: "polygon",
      data: {
        type: "polygon",
        points: (a.points || []).map((p) => ({
          x: (p.x / 100) * sheetSize.w,
          y: (p.y / 100) * sheetSize.h,
        })),
        color: a.color || "#3b82f6",
        fillColor: `${(a.color || "#3b82f6").slice(0, 7)}59`,
        lineWidth: 2,
        opacity: 1,
        rotation: 0, scaleX: 1, scaleY: 1,
        areaId: String(a._id),
        label: a.name,
      },
    }));
  }, [pageAreas, sheetSize]);

  // canvas shapes (pixels) -> areas (percent)
  const saveFromCanvas = async (issueInfo) => {
    const objs = (issueInfo && issueInfo.objects) || [];
    const closed = objs
      .map((o) => o.data || {})
      .filter((d) => ["polygon", "area", "rectangle", "highlightRect"].includes(d.type));
    if (!sheetSize.w || !sheetSize.h) {
      message.error("The sheet has not finished loading");
      return "";
    }
    const toPct = (pts) => pts.map((p) => ({
      x: Math.max(0, Math.min(100, (p.x / sheetSize.w) * 100)),
      y: Math.max(0, Math.min(100, (p.y / sheetSize.h) * 100)),
    }));
    const rectPoints = (d) => ([
      { x: d.x, y: d.y }, { x: d.x + d.width, y: d.y },
      { x: d.x + d.width, y: d.y + d.height }, { x: d.x, y: d.y + d.height },
    ]);
    let updated = 0;
    let created = 0;
    let notAdded = 0;
    for (const d of closed) {
      const pts = toPct(d.points && d.points.length ? d.points : rectPoints(d));
      if (pts.length < 3) continue;
      const color = (d.fillColor && d.fillColor !== "transparent" ? d.fillColor : d.color || "#3b82f6").slice(0, 7);
      try {
        if (d.areaId) {
          await updateArea(projectId, d.areaId, { points: pts, color });
          updated += 1;
        } else if (!canAdd) {
          // A new shape is an ADD (levelsAreasAdd), which this person lacks.
          notAdded += 1;
        } else {
          await createArea(projectId, {
            name: d.label || `Area ${areas.length + created + 1}`,
            color, points: pts, sheetImageUrl: sheetUrl,
            pageId: activeLevel ? activeLevel._id : undefined,
          });
          created += 1;
        }
      } catch (e) {
        message.error(e.message || "Could not save an area");
      }
    }
    message.success(`${updated} area(s) reshaped, ${created} added`);
    if (notAdded) message.warning(`${notAdded} new shape(s) not saved - adding areas needs the Levels & Areas add permission`, 8);
    setCanvasOpen(false);
    load();
    return "";
  };

  const selected = useMemo(
    () => areas.find((a) => String(a._id) === String(selectedId)) || null,
    [areas, selectedId]
  );

  // Percentages in, percentages out - the polygon must survive any
  // render size, so nothing here is measured in pixels.
  const pointFromEvent = (e) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const r = svg.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100)),
      y: Math.max(0, Math.min(100, ((e.clientY - r.top) / r.height) * 100)),
    };
  };

  const onSvgClick = (e) => {
    if (!drawing) return;
    const p = pointFromEvent(e);
    if (p) setDraft((d) => [...d, p]);
  };

  const finishDraft = async () => {
    if (draft.length < 3) {
      message.warning("An area needs at least three points");
      return;
    }
    if (!newName.trim()) {
      message.warning("Give the area a name");
      return;
    }
    try {
      await createArea(projectId, {
        name: newName.trim(), color: newColor, points: draft, sheetImageUrl: sheetUrl,
        pageId: activeLevel ? activeLevel._id : undefined,
      });
      message.success(`"${newName.trim()}" added`);
      setDraft([]); setDrawing(false); setNewName("");
      load();
    } catch (e) {
      message.error(e.message || "Could not save the area");
    }
  };

  const patch = async (id, body) => {
    try {
      await updateArea(projectId, id, body);
      load();
    } catch (e) {
      message.error(e.message || "Could not update the area");
    }
  };

  const decide = async (id, decision) => {
    try {
      await decideArea(projectId, id, decision);
      message.success(decision === "reject" ? "Rejected" : "Validated - it is on the sheet now");
      load();
    } catch (e) {
      message.error(e.message || "Could not record the decision");
    }
  };

  // Queue a sheet for a Claude session to read the zones off.
  //
  // `body` decides HOW MUCH: a document id alone means every page of the
  // set, one task each; adding `page` means just that sheet. Passing the
  // document rather than a bare image URL is what lets the zones it
  // finds stay attached to the controlled document they came from.
  const queueExtract = async (body, title) => {
    try {
      const r = await extractAreas(projectId, body);
      Modal.info({
        title,
        content: r.message || "The areas it finds will appear here for you to validate.",
      });
    } catch (e) {
      message.error(e.message || "Could not queue the sheet");
    }
  };

  const runExtract = async () => {
    if (!sheetDocId && !sheetUrl.trim()) {
      message.warning("Point at a sheet first - paste its URL, or pick one from Document Control");
      return;
    }
    // A loose image URL with no document behind it: read that, and that
    // is all there is to read.
    if (!sheetDocId) {
      return queueExtract({ imageUrl: sheetUrl.trim() }, "Sheet queued for analysis");
    }
    const count = (currentSheet && currentSheet.pageCount) || 1;
    if (count <= 1) {
      return queueExtract({ docControlId: sheetDocId }, "Sheet queued for analysis");
    }
    // A SET. Reading every sheet is usually what you want, but somebody
    // looking at sheet 2 because sheet 1 is already done should not have
    // to re-read sheet 1 to get it.
    return Modal.confirm({
      title: `"${currentSheet.name}" has ${count} sheets`,
      content: `Read all ${count}, or just the one you are looking at (sheet ${pageIdx + 1})? `
        + "Each sheet is read separately, and everything found waits for you to validate it.",
      okText: `Read all ${count}`,
      cancelText: `Just sheet ${pageIdx + 1}`,
      onOk: () => queueExtract({ docControlId: sheetDocId }, `${count} sheets queued for analysis`),
      onCancel: () => queueExtract(
        { docControlId: sheetDocId, page: pageIdx + 1 },
        `Sheet ${pageIdx + 1} queued for analysis`
      ),
    });
  };

  const polygon = (pts) => (pts || []).map((p) => `${p.x},${p.y}`).join(" ");

  // ---- level actions ------------------------------------------------------
  const onAddLevel = async () => {
    try {
      const r = await addLevel(projectId, {});
      await loadLevels();
      setLevelIdx((levels.pages || []).length);
      message.success(`${r.page.name} added${r.page.areasFrom === "first" ? ` - it uses ${(levels.pages[0] || {}).name || "the first level"}'s areas until you give it its own` : ""}`);
    } catch (e) {
      message.error(e.message || "Could not add a level");
    }
  };
  const onRenameLevel = async () => {
    const name = levelName.trim();
    if (!activeLevel || !name || name === activeLevel.name) { setLevelName(activeLevel ? activeLevel.name : ""); return; }
    try {
      const r = await updateLevel(projectId, activeLevel._id, { name });
      const m = r.moved || {};
      const bits = [
        m.activities ? `${m.activities} activit${m.activities === 1 ? "y" : "ies"}` : "",
        m.issues ? `${m.issues} issue(s)` : "",
        m.checklists ? `${m.checklists} checklist item(s)` : "",
      ].filter(Boolean);
      message.success(`Renamed to "${name}"${bits.length ? ` - also renamed on ${bits.join(", ")}` : ""}`);
      await loadLevels();
      load();
    } catch (e) {
      message.error(e.message || "Could not rename the level");
      setLevelName(activeLevel.name);
    }
  };
  // SAME SHEET AND AREAS AS ANOTHER LEVEL (owner, 2026-10-10: "level1 also
  // need to have the same option to copy sheet and areas from any other
  // level"). The server copies the sheet and points the areas; a level's own
  // areas are hidden while it shows another's, never deleted - so it says so
  // before doing it.
  const onUseLevel = (v) => {
    const src = (levels.pages || []).find((p) => String(p._id) === String(v));
    const go = async () => {
      try {
        await updateLevel(projectId, activeLevel._id, { useLevel: v === "own" ? null : v });
        message.success(v === "own"
          ? `${activeLevel.name} shows its own sheet and areas again`
          : `${activeLevel.name} now shows ${src ? src.name : "that level"}'s sheet and areas`);
        await loadLevels();
      } catch (e) {
        message.error(e.message || "Could not change where this level's sheet and areas come from");
      }
    };
    const own = activeLevel.ownAreaCount || 0;
    if (v !== "own" && own > 0 && activeLevel.areasFrom === "own") {
      Modal.confirm({
        title: `Show ${src ? src.name : "that level"}'s sheet and areas on ${activeLevel.name}?`,
        content: `${activeLevel.name} has ${own} area${own === 1 ? "" : "s"} of its own. They are hidden while it shows ${src ? src.name : "the other level"}'s - not deleted - and come back when you switch it to its own sheet and areas again.`,
        okText: "Show them",
        onOk: go,
      });
      return;
    }
    go();
  };
  const onRemoveLevel = async () => {
    try {
      await removeLevel(projectId, activeLevel._id);
      message.success(`${activeLevel.name} removed`);
      setLevelIdx((i) => Math.max(0, i - 1));
      await loadLevels();
    } catch (e) {
      message.error(e.message || "Could not remove the level");
    }
  };
  const firstLevelName = ((levels.pages || [])[0] || {}).name || "Level 1";

  // PRINT AND EXCEL (owner, 2026-10-13): every level with its drawing, legend,
  // areas, subsystems and the systems tagged in each - read only.
  const [exporting, setExporting] = useState("");
  const projectTitle = (projects.find((p) => String(p._id) === String(projectId)) || {}).title || "Project";
  const exportModel = async () => {
    const byLevel = await listAreaSystemsByLevel(projectId);
    return buildLevelsExport({ pages: levels.pages || [], zones: areas, legend, sheets, byLevel });
  };
  const onPrint = async () => {
    // Opened before the data arrives, so the browser counts it as the click's window.
    const w = window.open("", "_blank");
    if (!w) { message.error("The browser blocked the print window - allow pop-ups for this site and try again"); return; }
    setExporting("print");
    try {
      const model = await exportModel();
      w.document.open();
      w.document.write(printHtml(model, { projectName: projectTitle, origin: window.location.origin }));
      w.document.close();
      const imgs = [...w.document.images];
      await Promise.all(imgs.map((im) => (im.complete ? null : new Promise((ok) => { im.onload = ok; im.onerror = ok; }))));
      w.focus();
      w.print();
    } catch (e) {
      w.close();
      message.error(e.message || "Could not prepare the print");
    } finally {
      setExporting("");
    }
  };
  const onExportExcel = async () => {
    setExporting("excel");
    try {
      const [model, XLSX] = await Promise.all([exportModel(), import("xlsx")]);
      const file = downloadWorkbook(XLSX, model, { projectName: projectTitle });
      message.success(`Saved ${file}`);
    } catch (e) {
      message.error(e.message || "Could not export");
    } finally {
      setExporting("");
    }
  };

  return (
    <div className="personal-area">
      <div className="content-bg p-2">
        <div className="container-fluid px-3">
          <CoordinationBreadcrumbs current="Levels, Areas & Systems" />

          {viewDenied && (
            <div style={{ background: "#fff1f0", border: "1px solid #ffccc7", borderRadius: 6, padding: "10px 14px", marginBottom: 12, fontSize: 13 }}>
              You do not have permission to view Levels, Areas &amp; Systems on this project. A company admin can grant it in user permissions.
            </div>
          )}
          <div className="d-flex justify-content-between align-items-start flex-wrap mb-3">
            <div>
              <h4 className="mb-1">Levels, Areas &amp; Systems</h4>
              <div style={{ color: "#888", fontSize: 13, maxWidth: 620 }}>
                One page per level, each on its own drawing. The levels and areas
                here are the ones the schedule, shop drawings, issues and
                construction tracking offer. Colouring an area colours the work
                tied to it. Each area holds its systems; the model elements
                tagged to it in construction tracking suggest more.
                {!canEdit && !canAdd && !canDelete && " Your view is read-only; the project owner maintains these."}
              </div>
            </div>
            <div className="d-flex" style={{ gap: 8 }}>
              {/* WHO IS EMAILED ON AN OWNER/GC SIGN-OFF, in the top bar like the
                  shop drawings' recipients (owner, 2026-10-21) - one list for
                  every level, area and system, shown to whoever may sign off. */}
              <OwnerSignOffRecipients projectId={projectId} canSign={ownerSO.canSign} variant="button" />
              {/* Owner-side only, mirroring the API: a partner can be GIVEN
                  a link, never hand one out. */}
              {canEdit && (
                <Tooltip title="Send a read-only link to this page - to a partner on the project, or to anyone by email. No account needed to open it.">
                  <Button icon={<ShareAltOutlined />} onClick={() => setShareOpen(true)}>Share</Button>
                </Tooltip>
              )}
              <Tooltip title="Print every level: its drawing with the areas on it, the legend, and the systems in each area and subsystem">
                <Button icon={<PrinterOutlined />} disabled={!projectId || !(levels.pages || []).length} loading={exporting === "print"} onClick={onPrint} data-testid="areas-print">Print</Button>
              </Tooltip>
              <Tooltip title="Download levels, areas, legend, subsystems and systems with their tagged elements as an Excel workbook">
                <Button icon={<FileExcelOutlined />} disabled={!projectId || !(levels.pages || []).length} loading={exporting === "excel"} onClick={onExportExcel} data-testid="areas-export-excel">Export Excel</Button>
              </Tooltip>
              <Button onClick={() => history.push(`/scheduler/${projectId}`)}>Schedule</Button>
              {/* The sheet under these areas is also indexed for search,
                  so the same drawing answers "where is P-101" - in the
                  sheets and in the model (docs/2d-3d-search.md). */}
              <Tooltip title="One query across this project's sheets and its model.">
                <Button
                  icon={<SearchOutlined />}
                  disabled={!projectId}
                  onClick={() => history.push(`/split-search?projectId=${projectId}`)}
                >
                  Find in Sheets &amp; Model
                </Button>
              </Tooltip>
              <Tooltip title="Everything on this project that is not inside any area yet - activities, milestones, issues and checklist items.">
                <Button onClick={() => setUnplacedOpen(true)}>
                  No area yet
                  {coverage && coverage.unplacedCount > 0 && (
                    <Badge
                      count={coverage.unplacedCount}
                      overflowCount={9999}
                      style={{ backgroundColor: "#8c8c8c", marginLeft: 6 }}
                    />
                  )}
                </Button>
              </Tooltip>
              {canEdit && (
                <>
                  <Tooltip title="Read the grid bubbles and the key plan off every drawing, place each zone on the building grid, pair it with the older zones it replaces, and file activities that lost their zone">
                    <Button onClick={async () => {
                      try {
                        const r = await fetch(`${url}/projects/${projectId}/areas/grid-map`, { method: "POST", headers: { ...auth(), "Content-Type": "application/json" }, body: JSON.stringify({ relinkActivities: true }) });
                        const b = await r.json();
                        if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`);
                        message.success(`Mapped ${b.zones.length} zones to the grid - ${b.pairs.length} old/new pairs, ${b.relinks.length} activities filed${b.conflicts.length ? `, ${b.conflicts.length} level conflicts` : ""}`);
                        load();
                      } catch (e) { message.error(e.message || "Grid mapping failed"); }
                    }}>
                      Map to building grid
                    </Button>
                  </Tooltip>
                </>
              )}
              {canAdd && (
                <>
                  <Button icon={<ScanOutlined />} onClick={runExtract}>
                    Read areas off the sheet
                  </Button>
                </>
              )}
              {canEdit && (
                <>
                  <Tooltip title={sharedLevel ? `This level shows ${activeLevel.areasFromName || firstLevelName}'s areas - switch it to its own areas to draw here` : "Open the sheet in the markup canvas you already use - the same shape tools and fill-colour picker. Existing areas come in as editable shapes."}>
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      disabled={!sheetUrl || sharedLevel}
                      onClick={() => setCanvasOpen(true)}
                    >
                      Edit shapes on the canvas
                    </Button>
                  </Tooltip>
                </>
              )}
              {canAdd && (
                <>
                  <Button
                    type={drawing ? "primary" : "default"}
                    icon={<PlusOutlined />}
                    disabled={sharedLevel}
                    onClick={() => { setDrawing((d) => !d); setDraft([]); }}
                  >
                    {drawing ? "Drawing - click the sheet" : "Quick draw"}
                  </Button>
                </>
              )}
            </div>
          </div>

          {projectId && (levels.pages || []).length > 0 && !asOf && (
            <div className="mb-3" style={{ border: "1px solid #eef0f3", borderRadius: 8, padding: "10px 12px", background: "#fafbfc" }}>
              <div className="d-flex align-items-center flex-wrap" style={{ gap: 6 }}>
                <span style={{ fontWeight: 600, marginRight: 6 }}>Levels</span>
                {levels.pages.map((pg, i) => (
                  <Tooltip
                    key={pg._id}
                    title={pg.areasFrom && pg.areasFrom !== "own" ? `Shows ${pg.areasFromName || firstLevelName}'s sheet and areas` : `${(pg.areaIds || []).length} area(s)`}
                  >
                    <Button size="small" type={i === levelIdx ? "primary" : "default"} onClick={() => setLevelIdx(i)}>
                      {pg.name}
                      <span style={{ opacity: 0.65, marginLeft: 4 }}>
                        {pg.areasFrom && pg.areasFrom !== "own" ? `(= ${pg.areasFromName || firstLevelName})` : `(${(pg.areaIds || []).length})`}
                      </span>
                    </Button>
                  </Tooltip>
                ))}
                {canAdd && (
                  <Button size="small" icon={<PlusOutlined />} onClick={onAddLevel}>Add level</Button>
                )}
              </div>
              {activeLevel && (canEdit || canDelete) && (
                <div className="d-flex align-items-center flex-wrap mt-2" style={{ gap: 8 }}>
                  {canEdit && (
                  <Input
                    size="small"
                    addonBefore="Name"
                    style={{ maxWidth: 260 }}
                    value={levelName}
                    onChange={(e) => setLevelName(e.target.value)}
                    onPressEnter={onRenameLevel}
                    onBlur={onRenameLevel}
                  />
                  )}
                  {canEdit && (levels.pages || []).length > 1 && (
                    <Select
                      size="small"
                      style={{ minWidth: 260 }}
                      value={activeLevel.areasFromPageId || "own"}
                      onChange={onUseLevel}
                      options={[
                        { value: "own", label: "Its own sheet & areas" },
                        ...(levels.pages || [])
                          .filter((p) => String(p._id) !== String(activeLevel._id))
                          .map((p) => ({
                            value: p._id,
                            label: `Same sheet & areas as ${p.name}`,
                            // One step only: a level that itself shows another's
                            // cannot be a source (the server refuses it too).
                            disabled: !!(p.areasFrom && p.areasFrom !== "own"),
                          })),
                      ]}
                    />
                  )}
                  {canDelete && levelIdx > 0 && (
                    <Popconfirm title={`Remove ${activeLevel.name}?`} onConfirm={onRemoveLevel}>
                      <Button size="small" danger icon={<DeleteOutlined />}>Remove level</Button>
                    </Popconfirm>
                  )}
                  {canEdit && (
                  <span style={{ color: "#888", fontSize: 12 }}>
                    {activeLevel.sheetImageUrl
                      ? "Pick a different sheet below to change this level's drawing."
                      : "No drawing for this level yet - pick a sheet below or import one."}
                  </span>
                  )}
                </div>
              )}
              {activeLevel && !canEdit && sharedLevel && (
                <div style={{ color: "#888", fontSize: 12, marginTop: 6 }}>{activeLevel.name} shows {activeLevel.areasFromName || firstLevelName}'s sheet and areas.</div>
              )}
              {!levels.saved && canEdit && (
                <div style={{ color: "#888", fontSize: 12, marginTop: 6 }}>
                  These levels were set up from the drawings already on this project. Rename one, add one or change a drawing and they are saved.
                </div>
              )}
            </div>
          )}

          <div className="d-flex align-items-center flex-wrap mb-3" style={{ gap: 8 }}>
            <Select filterOption={matchOption}
              showSearch
              optionFilterProp="label"
              placeholder="Project"
              style={{ minWidth: 240 }}
              value={projectId || undefined}
              onChange={(v) => { setProjectId(v); setSheetDocId(""); setSheetUrl(""); }}
              options={projects.map((p) => ({ value: String(p._id), label: p.title }))}
            />
            {/* WHAT IS ON THIS LEVEL: its own page, not the set's first one
                (owner, 2026-10-10: "no thumbnail that shows the content of
                that page"). Hover for a larger view. */}
            {sheetUrl && (
              <Tooltip
                placement="bottomLeft"
                title={<img src={sheetUrl} alt="" style={{ width: 360, background: "#fff", display: "block" }} />}
                overlayInnerStyle={{ padding: 4, background: "#fff" }}
              >
                <div style={{ width: 56, height: 40, border: "1px solid #d9d9d9", borderRadius: 4, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                  <img src={sheetUrl} alt="" style={{ maxWidth: "100%", maxHeight: "100%" }} />
                </div>
              </Tooltip>
            )}
            {/* THE DRAWING PICKER (owner, 2026-10-10): thumbnails, a search
                box, an Upload button at the far right of the list, and
                drawings uploaded here at the top. */}
            <Select filterOption={matchOption}
              showSearch
              optionFilterProp="search"
              optionLabelProp="plain"
              placeholder={sheets.length ? (activeLevel ? `Drawing for ${activeLevel.name}` : "Sheet") : "No sheets on this project yet"}
              style={{ minWidth: 340 }}
              dropdownStyle={{ minWidth: 420 }}
              listHeight={420}
              value={sheetDocId || undefined}
              onChange={chooseSheet}
              disabled={sharedLevel && canEdit}
              notFoundContent="No sheet or drawing matches"
              dropdownRender={(menu) => (
                <div>
                  <div style={{ display: "flex", alignItems: "center", padding: "4px 8px 8px", borderBottom: "1px solid #f0f0f0", marginBottom: 4 }}>
                    <span style={{ color: "#888", fontSize: 12 }}>
                      {sheets.length} sheet{sheets.length === 1 ? "" : "s"} - type to search
                    </span>
                    {canAdd && (
                      <Button
                        size="small"
                        type="primary"
                        icon={<UploadOutlined />}
                        loading={importing}
                        style={{ marginLeft: "auto" }}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => fileRef.current && fileRef.current.click()}
                      >
                        Upload sheet
                      </Button>
                    )}
                  </div>
                  {menu}
                </div>
              )}
              options={sheets.map((sh) => {
                const plain = `${sh.name}${sh.revisionNumber ? ` rev ${sh.revisionNumber}` : ""}`;
                const thumb = sh.drawable && !/\.pdf(\?|$)/i.test(sh.url || "") ? sh.url : "";
                return {
                  value: sh._id,
                  plain,
                  search: `${sh.name} ${sh.levelsAreas ? "levels areas" : ""}`,
                  label: (
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 56, height: 40, flex: "0 0 56px", border: "1px solid #eee", borderRadius: 4, background: "#fafafa", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                        {thumb
                          ? <img src={thumb} alt="" loading="lazy" style={{ maxWidth: "100%", maxHeight: "100%" }} />
                          : <FilePdfOutlined style={{ color: "#bbb", fontSize: 18 }} />}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{plain}</div>
                        <div style={{ fontSize: 11, color: "#888" }}>
                          {sh.levelsAreas ? <Tag color="blue" style={{ fontSize: 10, lineHeight: "16px", marginRight: 4 }}>Levels &amp; Areas</Tag> : null}
                          {sh.pageCount > 1 ? `${sh.pageCount} pages - one level each` : (sh.isPdf ? "PDF" : "Image")}
                        </div>
                      </div>
                    </div>
                  ),
                };
              })}
            />
            {currentSheet && currentSheet.pageCount > 1 && (
              <Select
                style={{ minWidth: 150 }}
                value={pageIdx}
                onChange={choosePage}
                optionLabelProp="plain"
                dropdownStyle={{ minWidth: 220 }}
                options={(currentSheet.pages || []).map((pgUrl, i) => ({
                  value: i,
                  plain: `Page ${i + 1} of ${currentSheet.pageCount}`,
                  label: (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <img src={pgUrl} alt="" loading="lazy" style={{ width: 56, height: 40, objectFit: "contain", border: "1px solid #eee", background: "#fff" }} />
                      <span>Page {i + 1} of {currentSheet.pageCount}</span>
                    </div>
                  ),
                }))}
              />
            )}
            {canAdd && (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.bmp"
                  style={{ display: "none" }}
                  onChange={(e) => onImportSheet(e.target.files && e.target.files[0])}
                />
                <Tooltip title="The drawing is filed in Document Control for this project, tagged Levels & Areas, then rendered here. A PDF of several pages becomes one level per page.">
                  <Button
                    icon={<UploadOutlined />}
                    loading={importing}
                    onClick={() => fileRef.current && fileRef.current.click()}
                  >
                    Upload sheet
                  </Button>
                </Tooltip>
              </>
            )}
          </div>

          {canAdd && (
            <div className="d-flex align-items-center flex-wrap mb-3" style={{ gap: 8 }}>
              {drawing && (
                <>
                  <Input
                    placeholder="Area name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    style={{ maxWidth: 220 }}
                  />
                  {PALETTE.map((c) => (
                    <span
                      key={c}
                      onClick={() => setNewColor(c)}
                      title={c}
                      style={{
                        width: 20, height: 20, borderRadius: 4, background: c, cursor: "pointer",
                        border: newColor === c ? "2px solid #111" : "1px solid #ddd", display: "inline-block",
                      }}
                    />
                  ))}
                  <Button type="primary" onClick={finishDraft} disabled={draft.length < 3}>
                    Save area ({draft.length} pts)
                  </Button>
                  <Button onClick={() => { setDraft([]); setDrawing(false); }}>Cancel</Button>
                </>
              )}
            </div>
          )}

          <AreaLegendStrip
            projectId={projectId}
            legend={legend}
            onSaved={() => getAreaLegend(projectId).then(setLegend).catch(() => {})}
          />

          {checkpoints.length > 0 && (
            <div
              className="d-flex align-items-center flex-wrap"
              style={{
                gap: 8, marginBottom: 12, padding: "7px 12px", fontSize: 13, borderRadius: 6,
                background: asOf ? "#fef3c7" : "#fff",
                border: `1px solid ${asOf ? "#fcd34d" : "#e5e7eb"}`,
              }}
            >
              <HistoryOutlined style={{ color: asOf ? "#b45309" : "#64748b" }} />
              <span style={{ color: asOf ? "#92400e" : "#475569" }}>Showing the map</span>
              <Select
                size="small"
                style={{ minWidth: 300 }}
                value={asOf}
                onChange={setAsOf}
                options={[
                  { value: "", label: "as it is now" },
                  ...checkpoints.map((c) => ({
                    value: c.date,
                    label: `as of ${c.date} — ${c.live} zone${c.live === 1 ? "" : "s"}`
                      + (c.added ? `, ${c.added} added` : "")
                      + (c.retired ? `, ${c.retired} retired` : "")
                      + (c.sheets.length ? ` · ${c.sheets[0]}` : ""),
                  })),
                ]}
              />
              {asOf && (
                <>
                  <span style={{ color: "#92400e" }}>
                    This is history — the zones that were agreed on {asOf}. Nothing here can be edited.
                  </span>
                  <Button size="small" onClick={() => setAsOf("")}>Back to now</Button>
                </>
              )}
            </div>
          )}

          {canEdit && retired.length > 0 && (
            <div
              style={{
                background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6,
                padding: "8px 14px", marginBottom: 14, fontSize: 13,
              }}
            >
              <span
                onClick={() => setRetiredOpen((v) => !v)}
                style={{ cursor: "pointer", color: "#475569" }}
              >
                <HistoryOutlined />{" "}
                <b>{retired.length}</b> retired zone{retired.length === 1 ? "" : "s"} — superseded by a
                newer drawing, kept as history{" "}
                <span style={{ color: "#2563eb" }}>{retiredOpen ? "hide" : "show"}</span>
              </span>
              {retiredOpen && (
                <div style={{ marginTop: 8 }}>
                  {retired.map((a) => (
                    <div key={a._id} className="d-flex align-items-center" style={{ gap: 8, marginBottom: 4 }}>
                      <span style={{ width: 12, height: 12, background: a.color || "#999", borderRadius: 3, display: "inline-block" }} />
                      <span style={{ color: "#64748b" }}>{a.code ? `${a.code} · ` : ""}{a.name}</span>
                      {a.retiredAt && (
                        <span style={{ color: "#94a3b8", fontSize: 12 }}>
                          retired {new Date(a.retiredAt).toLocaleDateString()}
                        </span>
                      )}
                      <Button
                        size="small"
                        onClick={async () => {
                          try {
                            await restoreArea(projectId, a._id);
                            message.success(`"${a.name}" is back on the drawing`);
                            load();
                          } catch (e) { message.error(e.message || "Could not restore it"); }
                        }}
                      >
                        Restore
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {canEdit && migrations.map((m) => (
            <AreaMapRevisionPanel
              key={m._id}
              projectId={projectId}
              migration={m}
              onDone={() => { load(); loadSheets(); }}
            />
          ))}

          {loosePending.length > 0 && canEdit && (
            <div style={{ background: "#fffbe6", border: "1px solid #ffe58f", borderRadius: 6, padding: "10px 14px", marginBottom: 14 }}>
              <b>Waiting for you</b>{" "}
              <Badge count={loosePending.length} style={{ backgroundColor: "#faad14" }} />
              <div style={{ color: "#7a5a00", fontSize: 13, margin: "4px 0 8px" }}>
                Read off a sheet. Nothing here is on the drawing until you validate it.
              </div>
              {loosePending.map((p) => (
                <div key={p._id} className="d-flex align-items-center" style={{ gap: 8, marginBottom: 4 }}>
                  <span style={{ width: 14, height: 14, background: p.color || "#999", borderRadius: 3, display: "inline-block" }} />
                  <b style={{ fontSize: 13 }}>{p.name}</b>
                  {p.level && <Tag>{p.level}</Tag>}
                  {p.matchHint && <span style={{ color: "#888", fontSize: 12 }}>matches “{p.matchHint}”</span>}
                  <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => decide(p._id, "approve")}>
                    Validate
                  </Button>
                  <Button size="small" danger icon={<CloseOutlined />} onClick={() => decide(p._id, "reject")}>
                    Reject
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="d-flex flex-wrap" style={{ gap: 16 }}>
            <div style={{ flex: "1 1 620px", minWidth: 320 }}>
              <div
                style={{
                  position: "relative", border: "1px solid #e5e7eb", borderRadius: 6,
                  background: sheetUrl ? "#fff" : "#fafafa", overflow: "hidden", minHeight: 420,
                }}
              >
                {sheetUrl ? (
                  <img src={sheetUrl} alt="sheet" style={{ width: "100%", display: "block" }} />
                ) : (
                  <div style={{ padding: 80, textAlign: "center", color: "#aaa" }}>
                    <Empty description="No sheet yet - paste a sheet image URL above, or read one off Document Control" />
                  </div>
                )}
                <svg
                  ref={svgRef}
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  onClick={onSvgClick}
                  style={{
                    position: "absolute", inset: 0, width: "100%", height: "100%",
                    cursor: drawing ? "crosshair" : "default",
                  }}
                >
                  {pageAreas.map((a) => (
                    <polygon
                      key={a._id}
                      points={polygon(a.points)}
                      fill={a.color || "#3b82f6"}
                      fillOpacity={String(a._id) === String(selectedId) ? Math.min(0.65, (a.opacity || 0.28) + 0.2) : (a.opacity || 0.28)}
                      stroke={a.color || "#3b82f6"}
                      strokeWidth="0.4"
                      vectorEffect="non-scaling-stroke"
                      style={{ cursor: "pointer" }}
                      onClick={(e) => { e.stopPropagation(); setSelectedId(a._id); }}
                    />
                  ))}
                  {draft.length > 0 && (
                    <polygon
                      points={polygon(draft)}
                      fill={newColor}
                      fillOpacity="0.25"
                      stroke={newColor}
                      strokeWidth="0.4"
                      strokeDasharray="1.5 1"
                      vectorEffect="non-scaling-stroke"
                    />
                  )}
                </svg>
              </div>
            </div>

            <div style={{ flex: "0 0 300px", minWidth: 250 }}>
              {!asOf && activeLevel && (
                <div style={{ border: "1px solid #dbeafe", background: "#f8fbff", borderRadius: 6, padding: "8px 10px", marginBottom: 10 }} data-testid="owner-signoff-level">
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{activeLevel.name}</div>
                  <OwnerSignOffToggle
                    toggle={findToggle(ownerSO.toggles, { kind: "level", level: activeLevel.name })}
                    canSign={ownerSO.canSign}
                    label="Level signed off by owner/GC"
                    onSet={({ date }) => ownerSOSet({ kind: "level", level: activeLevel.name, date })}
                    onClear={ownerSOClear}
                  />
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                    Each area and system below has its own switch. A signed-off level, area or system puts a blue star on the schedule and marks its issues and shop drawings.
                    {ownerSO.failed ? <b style={{ color: "#b45309" }}> Could not read the owner/GC sign-offs - the switches show off until the page is reloaded.</b> : null}
                  </div>
                </div>
              )}
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                <HistoryOutlined /> Drawn on
              </div>
              {/* The current composition only: a drawing whose zones were all
                  superseded by a newer one is history, reached through
                  "Showing the map" above - not listed beside today's map
                  with its old colours (owner, 2026-09-17). */}
              {(() => {
                const olderCount = asOf ? 0 : timeline.filter((g) => !(g.areas || []).some((x) => x.status !== "historic")).length;
                return olderCount > 0 ? (
                  <div style={{ color: "#888", fontSize: 12, marginBottom: 8 }}>
                    {olderCount} older drawing{olderCount === 1 ? "" : "s"} - pick a date in "Showing the map" to see {olderCount === 1 ? "it" : "them"}.
                  </div>
                ) : null;
              })()}
              {timeline.length === 0 ? (
                <div style={{ color: "#aaa", fontSize: 12, marginBottom: 12 }}>
                  Nothing drawn yet on this project.
                </div>
              ) : (
                timeline.filter((g) => asOf || (g.areas || []).some((x) => x.status !== "historic")).map((g) => ({ ...g, areas: asOf ? g.areas : g.areas.filter((x) => x.status !== "historic") })).map((g) => (
                  <div
                    key={g.key}
                    onClick={() => { if (g.sheetImageUrl) setSheetUrl(g.sheetImageUrl); }}
                    style={{
                      border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 10px",
                      marginBottom: 8, cursor: g.sheetImageUrl ? "pointer" : "default",
                      background: g.sheetImageUrl === sheetUrl ? "#f0f7ff" : "#fff",
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {g.sheetName || (g.sheetImageUrl || "").split("/").pop() || "Sheet"}
                    </div>
                    <div style={{ color: "#888", fontSize: 12 }}>
                      {new Date(g.lastDrawn).toLocaleDateString()} · {g.areas.length} area
                      {g.areas.length === 1 ? "" : "s"}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 5 }}>
                      {g.areas.slice(0, 14).map((a) => (
                        <Tooltip
                          key={a._id}
                          overlayStyle={{ maxWidth: 300 }}
                          overlayInnerStyle={{ padding: 10 }}
                          title={(
                            <AreaShapePreview
                              area={{ ...a, sheetImageUrl: g.sheetImageUrl, linkedCount: a.linked }}
                            />
                          )}
                        >
                          <span
                            style={{
                              width: 12, height: 12, borderRadius: 3, display: "inline-block",
                              background: a.color || "#999",
                              outline: a.status === "pending" ? "2px dotted #faad14" : "none",
                            }}
                          />
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                ))
              )}

              <div style={{ fontWeight: 600, fontSize: 13, margin: "14px 0 6px" }}>
                Areas
                {currentSheet && currentSheet.pageCount > 1 && (
                  <span style={{ fontWeight: 400, color: "#94a3b8" }}>
                    {" "}on sheet {pageIdx + 1}
                    {areas.length > pageAreas.length && ` · ${areas.length - pageAreas.length} on other sheets`}
                  </span>
                )}
              </div>
              {/* ISSUE LABELS NO AREA ANSWERS TO (owner, 2026-10-16: "you
                  should be able to link these 2 to a certain area"). Issues
                  raised before a zone was drawn say "XC-Utilities" or carry
                  PILOT-LINE_COORDINATION; adding the label to an area's
                  "Also known as" places them there (area sign-off filter). */}
              {!asOf && unmatchedLabels.length > 0 && (
                <div style={{ fontSize: 12, color: "#b45309", marginBottom: 6 }} data-testid="unmatched-labels">
                  Issue labels no area is called:{" "}
                  {unmatchedLabels.slice(0, 6).map((l, i) => (
                    <span key={`${l.kind}|${l.label}`}>
                      {i ? " · " : ""}<b>{l.label}</b> ({l.issues} issue{l.issues === 1 ? "" : "s"}{l.kind === "tag" ? ", tag" : ""})
                    </span>
                  ))}
                  {unmatchedLabels.length > 6 ? ` · ${unmatchedLabels.length - 6} more` : ""}
                  {canEdit ? ". Select an area and add the label under Also known as." : "."}
                </div>
              )}
              {!asOf && systemsData.totals && Number(systemsData.totals.tagged) > 0 && (
                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }} data-testid="systems-summary">
                  Systems suggested from <b>{Number(systemsData.totals.tagged).toLocaleString("en-US")}</b> model elements tagged to areas
                  {" "}({Number(systemsData.totals.mapped || 0).toLocaleString("en-US")} in a system,
                  {" "}{Number(systemsData.totals.unmapped || 0).toLocaleString("en-US")} in unmapped categories
                  {Number(systemsData.totals.notInModel) > 0 ? `, ${Number(systemsData.totals.notInModel).toLocaleString("en-US")} not in the live model` : ""})
                  {systemsData.orphaned && systemsData.orphaned.elements > 0
                    ? `; ${systemsData.orphaned.elements.toLocaleString("en-US")} tagged to ${systemsData.orphaned.zones} area(s) no longer on the map`
                    : ""}
                  {systemsData.refreshing ? " - updating..." : ""}
                </div>
              )}
              {!asOf && placementLine(systemsData.placement) && (
                <div
                  style={{ fontSize: 12, color: placementNeedsAction(systemsData.placement) ? "#b45309" : "#64748b", marginBottom: 6 }}
                  data-testid="systems-placement"
                >
                  {placementLine(systemsData.placement)}
                </div>
              )}
              {loading ? (
                <Spin />
              ) : pageAreas.length === 0 ? (
                <Empty description={areas.length ? "No areas on this sheet" : "No areas yet"} />
              ) : (
                pageAreas.map((a) => {
                  const isSel = String(a._id) === String(selectedId);
                  return (
                    <div
                      key={a._id}
                      onClick={() => setSelectedId(a._id)}
                      style={{
                        border: `1px solid ${isSel ? a.color || "#3b82f6" : "#e5e7eb"}`,
                        borderRadius: 6, padding: "8px 10px", marginBottom: 8, cursor: "pointer",
                        background: isSel ? "#fafcff" : "#fff",
                      }}
                    >
                      <div className="d-flex align-items-center" style={{ gap: 8 }}>
                        <span style={{ width: 14, height: 14, background: a.color || "#3b82f6", borderRadius: 3, display: "inline-block" }} />
                        <b style={{ fontSize: 13 }}>{a.name}</b>
                        {a.code && <Tag style={{ marginLeft: "auto" }}>{a.code}</Tag>}
                      </div>
                      <div style={{ color: "#888", fontSize: 12, marginTop: 2 }}>
                        {[a.level, a.discipline].filter(Boolean).join(" · ") || "no level set"}
                        {" · "}
                        {(a.ganttItemIds || []).length} linked activit
                        {(a.ganttItemIds || []).length === 1 ? "y" : "ies"}
                      </div>
                      {(a.aliases || []).length > 0 && (
                        <div style={{ color: "#64748b", fontSize: 12, marginTop: 2 }} data-testid="area-aliases">
                          Also known as: <b>{a.aliases.join(" · ")}</b>
                        </div>
                      )}
                      {/* Building grid (utils/sheetGridMap): the latest zone
                          first, its architectural areas from the title-block
                          key plan, and the zones of older drawings it replaced
                          underneath (owner, 2026-09-17). */}
                      {((a.dates || []).length > 0 || (a.archAreas || []).length > 0 || (a.grid && a.grid.label)) && (
                        <div style={{ fontSize: 12, marginTop: 3, color: "#334155" }}>
                          {(a.dates || []).map((d) => (
                            <Tag key={`${d.label}-${d.raw}`} color="green" style={{ marginRight: 4 }}>{d.label} {String(d.raw || "").slice(0, 10)}</Tag>
                          ))}
                          {(a.archAreas || []).length > 0 && (
                            <Tooltip title={(a.archOverlap || []).length
                              ? `${a.name} spans ${a.archOverlap.map((o) => `Area ${o.name}: ${Math.round((o.zoneShare || 0) * 100)}% of the zone, covering ${Math.round((o.areaShare || 0) * 100)}% of the area`).join("; ")}`
                              : "Architectural areas of the key plan this zone lies in"}>
                              <span>
                                Architectural: <b>{a.archAreas.map((x) => `Area ${x}`).join(" · ")}</b>
                              </span>
                            </Tooltip>
                          )}
                          {a.grid && a.grid.label && (
                            <span style={{ color: "#94a3b8" }} title="Grid lines this zone spans">
                              {(a.archAreas || []).length ? " · " : ""}grid {a.grid.label}
                            </span>
                          )}
                        </div>
                      )}
                      {(a.replaces || []).length > 0 && (
                        <div style={{ fontSize: 11.5, marginTop: 3, color: "#94a3b8", borderLeft: "2px solid #e2e8f0", paddingLeft: 6 }}>
                          Replaces on the older drawing:
                          {a.replaces.map((r) => (
                            <div key={String(r.areaId)} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                 title={`${r.name} - ${Math.round((r.overlap || 0) * 100)}% on the grid${r.sheetName ? `, ${r.sheetName}` : ""}`}>
                              {r.name} <span style={{ color: "#cbd5e1" }}>({Math.round((r.overlap || 0) * 100)}%{r.drawnAt ? `, ${new Date(r.drawnAt).toLocaleDateString()}` : ""})</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {!asOf && activeLevel && (
                        <div style={{ marginTop: 4 }} data-testid="owner-signoff-area">
                          <OwnerSignOffToggle
                            toggle={findToggle(ownerSO.toggles, { kind: "area", level: activeLevel.name, areaId: a._id, areaSetId: a.areaSetId })}
                            canSign={ownerSO.canSign}
                            label={`Area signed off by owner/GC`}
                            onSet={({ date }) => ownerSOSet({ kind: "area", level: activeLevel.name, areaId: a._id, date })}
                            onClear={ownerSOClear}
                          />
                          {(((systemsData.byId[String(a._id)] || {}).systems) || []).map((sys) => (
                            <div key={sys.name} style={{ paddingLeft: 14, marginTop: 2 }} data-testid="owner-signoff-system">
                              <OwnerSignOffToggle
                                toggle={findToggle(ownerSO.toggles, { kind: "system", level: activeLevel.name, areaId: a._id, areaSetId: a.areaSetId, system: sys.name })}
                                canSign={ownerSO.canSign}
                                label={sys.name}
                                onSet={({ date }) => ownerSOSet({ kind: "system", level: activeLevel.name, areaId: a._id, system: sys.name, date })}
                                onClear={ownerSOClear}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                      {!asOf && (
                        <div onClick={(e) => { if (isSel) e.stopPropagation(); }}>
                          <AreaSystemsPanel
                            projectId={projectId}
                            areaId={a._id}
                            row={systemsData.byId[String(a._id)]}
                            vocabulary={systemsData.vocabulary}
                            canEdit={canEdit}
                            compact={!isSel}
                            trackingHref={projectId ? `/tracking/${projectId}` : ""}
                            onChanged={(r) => onSystemsChanged(a._id, r)}
                          />
                        </div>
                      )}
                      {/* This area's shop drawings, each with its activity's full path. */}
                      {isSel && <AreaShopDrawings projectId={projectId} areaId={a._id} areaName={a.name} />}
                      {isSel && (canEdit || canDelete) && (
                        <div style={{ marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
                          {canEdit && (
                          <div className="d-flex align-items-center flex-wrap" style={{ gap: 6, marginBottom: 6 }}>
                            {PALETTE.map((c) => (
                              <Tooltip key={c} title="Recolour this area and everything tied to it">
                                <span
                                  onClick={() => patch(a._id, { color: c })}
                                  style={{
                                    width: 18, height: 18, borderRadius: 4, background: c, cursor: "pointer",
                                    border: a.color === c ? "2px solid #111" : "1px solid #ddd", display: "inline-block",
                                  }}
                                />
                              </Tooltip>
                            ))}
                          </div>
                          )}
                          {canEdit && (
                          <Select
                            mode="multiple"
                            size="small"
                            style={{ width: "100%" }}
                            placeholder="Activities this area covers"
                            optionFilterProp="label"
                            value={(a.ganttItemIds || []).map(String)}
                            onChange={(v) => patch(a._id, { ganttItemIds: v })}
                            options={activityOptions.map((it) => ({
                              value: String(it._id),
                              label: `${it.wbs ? `${it.wbs} ` : ""}${activityLabel(it)}`,
                              title: activityTitle(it),
                            }))}
                          />
                          )}
                          {canEdit && (
                          <Tooltip title="Other names issues use for this area - an issue whose area label or tag is one of these is placed in this area">
                            <Select
                              mode="tags"
                              size="small"
                              style={{ width: "100%", marginTop: 6 }}
                              placeholder="Also known as - issue labels or tags that mean this area"
                              aria-label="also known as"
                              tokenSeparators={[","]}
                              value={a.aliases || []}
                              onChange={(v) => patch(a._id, { aliases: v })}
                              options={aliasOptions(a)}
                            />
                          </Tooltip>
                          )}
                          <div className="d-flex" style={{ gap: 6, marginTop: 6 }}>
                            {canEdit && (
                            <Button
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() => {
                                const n = window.prompt("Area name", a.name);
                                if (n && n.trim()) patch(a._id, { name: n.trim() });
                              }}
                            >
                              Rename
                            </Button>
                            )}
                            {canDelete && (
                            <Popconfirm
                              title="Remove this area?"
                              onConfirm={async () => {
                                await deleteArea(projectId, a._id);
                                setSelectedId(null);
                                load();
                              }}
                            >
                              <Button size="small" danger icon={<DeleteOutlined />}>Remove</Button>
                            </Popconfirm>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      <Drawer
        title="Not inside any area yet"
        placement="right"
        width={520}
        open={unplacedOpen}
        onClose={() => setUnplacedOpen(false)}
      >
        {!coverage ? (
          <Spin />
        ) : (
          <>
            <div style={{ marginBottom: 12, fontSize: 13, color: "#666" }}>
              {["activities", "milestones", "issues", "checklists"].map((k) => (
                <div key={k}>
                  <b>{coverage.placed[k]}</b> of {coverage.totals[k]} {k} sit inside an area
                </div>
              ))}
            </div>
            {canEdit && (
              <div className="d-flex" style={{ gap: 8, marginBottom: 12 }}>
                <Select filterOption={matchOption}
                  showSearch
                  optionFilterProp="label"
                  placeholder="Put the ticked items into..."
                  style={{ flex: 1 }}
                  value={assignTo || undefined}
                  onChange={setAssignTo}
                  options={(coverage.areas || []).map((a) => ({ value: a._id, label: a.name }))}
                />
                <Button type="primary" disabled={!pickedTotal} onClick={doAssign}>
                  Add {pickedTotal || ""}
                </Button>
              </div>
            )}
            {["activities", "milestones", "issues", "checklists"].map((kind) => {
              const rows = (coverage.unplaced || {})[kind] || [];
              if (!rows.length) return null;
              return (
                <div key={kind} style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, textTransform: "capitalize" }}>
                    {kind} ({rows.length})
                  </div>
                  <List
                    size="small"
                    bordered
                    dataSource={rows.slice(0, 200)}
                    renderItem={(r) => (
                      <List.Item style={{ padding: "4px 8px" }}>
                        {canEdit && (
                          <Checkbox
                            checked={picked[kind].includes(r.id)}
                            onChange={() => togglePick(kind, r.id)}
                            style={{ marginRight: 8 }}
                          />
                        )}
                        <span style={{ fontSize: 12 }}>{r.label}</span>
                      </List.Item>
                    )}
                  />
                  {rows.length > 200 && (
                    <div style={{ fontSize: 12, color: "#999", marginTop: 4 }}>
                      showing the first 200 of {rows.length}
                    </div>
                  )}
                </div>
              );
            })}
            {coverage.unplacedCount === 0 && (
              <Empty description="Everything on this project sits inside an area" />
            )}
          </>
        )}
      </Drawer>

      <Modal
        open={canvasOpen}
        onCancel={() => setCanvasOpen(false)}
        footer={null}
        width="calc(96 * var(--vw))"
        style={{ top: 16 }}
        bodyStyle={{ padding: 0, height: "calc(86 * var(--vh))" }}
        destroyOnClose
        title={`Levels, Areas & Systems - ${activeLevel ? activeLevel.name : "markup canvas"}`}
      >
        {canvasOpen && sheetUrl && (
          <div style={{ height: "calc(86 * var(--vh))" }}>
            <IssueTool
              isOpen
              embedded
              hideHeader
              fitOnLoad
              canEdit={canEdit}
              documentUrl={sheetUrl}
              sheetRef={sheetDocId ? { projectId, docId: sheetDocId, page: pageIdx + 1 } : null}
              documentName="Levels & Areas"
              initialIssueObjects={seededObjects}
              onSaveIssue={saveFromCanvas}
              onClose={() => setCanvasOpen(false)}
            />
          </div>
        )}
      </Modal>

      <ShareResourceModal
        type="areas"
        id={projectId}
        projectId={projectId}
        title={(projects.find((p) => String(p._id) === String(projectId)) || {}).title || ""}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
      />
    </div>
  );
}
